import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { recruitmentService } from '../services/recruitmentService';
import { projectService } from '../services/projectService';
import {
  CCard, CCardBody,
  CRow, CCol,
  CTable, CTableHead, CTableBody, CTableRow, CTableHeaderCell, CTableDataCell,
  CBadge, CButton, CFormSelect, CFormInput,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CFormLabel, CFormTextarea, CSpinner,
} from '@coreui/react';
import AgingBadge from '../components/AgingBadge';
import { ClipboardList, Pencil, History, Filter, X } from 'lucide-react';

const STATUS_LABELS = {
  pending_approval: 'Pending Approval', approved_not_started: 'Approved / Not Started',
  job_posting: 'Job Posting', interview_scheduled: 'Interview Scheduled',
  waiting_interview_result: 'Waiting for Result', offer_sent: 'Offer Sent',
  accepted_waiting_start: 'Accepted / Waiting', started: 'Started', cancelled_on_hold: 'Cancelled',
};
const ALL_STATUSES = Object.keys(STATUS_LABELS);

export default function RecruitmentCasesPage() {
  const { authAxios } = useAuth();
  const [cases, setCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [editCase, setEditCase] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', notes: '', start_date: '' });
  const [saving, setSaving] = useState(false);
  const [historyCase, setHistoryCase] = useState(null);

  const api = authAxios();

  const fetchCases = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterProject) params.project_id = filterProject;
      const [casesRes, projRes, posRes] = await Promise.all([
        recruitmentService.getCases(api, params),
        projectService.getProjects(api),
        projectService.getPositions(api),
      ]);
      setCases(casesRes.data);
      setProjects(projRes.data);
      const posMap = {};
      posRes.data.forEach(p => { posMap[p.id] = p.name; });
      setPositions(posMap);
    } catch { toast.error('Failed to load cases'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCases(); /* eslint-disable-next-line */ }, [filterStatus, filterProject]);

  const projMap = {};
  projects.forEach(p => { projMap[p.id] = p.name; });

  const openEdit = (c) => {
    setEditCase(c);
    setEditForm({ status: c.status, notes: '', start_date: c.start_date || '' });
  };

  const handleSave = async () => {
    if (!editCase) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.status && editForm.status !== editCase.status) payload.status = editForm.status;
      if (editForm.notes) payload.notes = editForm.notes;
      if (editForm.start_date) payload.start_date = editForm.start_date;
      await recruitmentService.updateCase(api, editCase.id, payload);
      toast.success('Case updated');
      setEditCase(null);
      fetchCases();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const hasFilter = filterStatus || filterProject;

  return (
    <div data-testid="recruitment-cases-page">
      <div className="mb-4">
        <h4 className="fw-bold mb-1 d-flex align-items-center gap-2"><ClipboardList size={22} /> Recruitment Cases</h4>
        <small className="text-body-secondary">Manage and track recruitment progress</small>
      </div>

      {/* Filters */}
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody className="py-2">
          <CRow className="g-2 align-items-center">
            <CCol xs="auto"><Filter size={14} className="text-body-secondary" /></CCol>
            <CCol sm={3}>
              <CFormSelect size="sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="cases-filter-status">
                <option value="">All Statuses</option>
                {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={3}>
              <CFormSelect size="sm" value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="cases-filter-project">
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </CFormSelect>
            </CCol>
            {hasFilter && (
              <CCol xs="auto">
                <CButton color="ghost-secondary" size="sm" onClick={() => { setFilterStatus(''); setFilterProject(''); }} data-testid="cases-clear-filters">Clear</CButton>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Table */}
      <CCard className="border-0 shadow-sm" data-testid="cases-table">
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="primary" /></div>
          ) : (
            <CTable hover small className="mb-0">
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase">Project</CTableHeaderCell>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase">Position</CTableHeaderCell>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase text-center">Qty</CTableHeaderCell>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase">Status</CTableHeaderCell>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase">Aging</CTableHeaderCell>
                  <CTableHeaderCell className="small text-body-secondary text-uppercase text-center">Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {cases.length === 0 ? (
                  <CTableRow><CTableDataCell colSpan={6} className="text-center py-4 text-body-secondary small">No cases found</CTableDataCell></CTableRow>
                ) : cases.map(c => (
                  <CTableRow key={c.id}>
                    <CTableDataCell className="small fw-medium">{projMap[c.project_id] || 'Unknown'}</CTableDataCell>
                    <CTableDataCell className="small">{positions[c.position_id] || 'Unknown'}</CTableDataCell>
                    <CTableDataCell className="small text-center font-monospace">{c.qty}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="light" textColor="dark" className="small border">{STATUS_LABELS[c.status] || c.status}</CBadge>
                    </CTableDataCell>
                    <CTableDataCell><AgingBadge days={c.aging_days} group={c.aging_group} /></CTableDataCell>
                    <CTableDataCell className="text-center">
                      <CButton color="ghost-secondary" size="sm" className="p-1" onClick={() => openEdit(c)} data-testid={`edit-case-${c.id}`}>
                        <Pencil size={14} />
                      </CButton>
                      <CButton color="ghost-secondary" size="sm" className="p-1" onClick={() => setHistoryCase(c)} data-testid={`history-case-${c.id}`}>
                        <History size={14} />
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>

      {/* Edit Modal */}
      <CModal visible={!!editCase} onClose={() => setEditCase(null)} data-testid="edit-case-dialog">
        <CModalHeader><CModalTitle className="fw-bold">Update Case</CModalTitle></CModalHeader>
        <CModalBody>
          <p className="small text-body-secondary mb-3">{projMap[editCase?.project_id]} - {positions[editCase?.position_id]}</p>
          <div className="mb-3">
            <CFormLabel className="small fw-medium">Status</CFormLabel>
            <CFormSelect value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} data-testid="edit-case-status">
              {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </CFormSelect>
          </div>
          {(editForm.status === 'accepted_waiting_start' || editForm.status === 'started') && (
            <div className="mb-3">
              <CFormLabel className="small fw-medium">Start Date</CFormLabel>
              <CFormInput type="date" value={editForm.start_date} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} data-testid="edit-case-start-date" />
            </div>
          )}
          <div className="mb-3">
            <CFormLabel className="small fw-medium">Notes</CFormLabel>
            <CFormTextarea rows={3} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Add update notes..." data-testid="edit-case-notes" />
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="ghost" onClick={() => setEditCase(null)} data-testid="edit-case-cancel">Cancel</CButton>
          <CButton color="primary" onClick={handleSave} disabled={saving} data-testid="edit-case-save">{saving ? 'Saving...' : 'Save Changes'}</CButton>
        </CModalFooter>
      </CModal>

      {/* History Modal */}
      <CModal visible={!!historyCase} onClose={() => setHistoryCase(null)} data-testid="history-dialog">
        <CModalHeader><CModalTitle className="fw-bold">Status History</CModalTitle></CModalHeader>
        <CModalBody style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <p className="small text-body-secondary mb-3">{projMap[historyCase?.project_id]} - {positions[historyCase?.position_id]}</p>
          {historyCase?.status_history?.map((h, i) => (
            <div key={i} className="d-flex gap-2 mb-3">
              <div className="rounded-circle bg-primary mt-1 flex-shrink-0" style={{ width: 8, height: 8 }} />
              <div>
                <div className="small fw-semibold">{STATUS_LABELS[h.status] || h.status}</div>
                <div className="small text-body-secondary">
                  {new Date(h.changed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                {h.notes && <div className="small text-body-secondary">{h.notes}</div>}
              </div>
            </div>
          ))}
        </CModalBody>
      </CModal>
    </div>
  );
}
